import useJsonLd from '../../hooks/useJsonLd';
import businessIdentity from '../../config/businessIdentity.json';
import {
  HOME_DESIGN_COUNT,
  SUBURB_GUIDE_COUNT,
  BLOG_POST_COUNT,
  HBH_EPISODE_COUNT,
} from '../../data/siteStats';

const SITE_URL = 'https://buildpilot.com.au';

/**
 * <AIOverviewSchema />
 * --------------------
 * Layers on top of the visible <QuickAnswer> block to maximise the
 * likelihood of being CITED by AI Overviews (Google SGE), ChatGPT,
 * Perplexity, Claude and Bing Copilot.
 *
 * Emits four things:
 *
 *   1) A WebPage.speakable node targeting the QuickAnswer CSS
 *      selector (`[data-testid="quick-answer"]`). Voice assistants
 *      (Google Assistant, Siri) read that block aloud verbatim.
 *
 *   2) A QAPage.mainEntity Question -> acceptedAnswer.Text tuple.
 *      This is the *specific* structure LLMs parse when deciding
 *      which page to attribute a factual answer back to.
 *
 *   3) A Dataset entity representing BuildPilot's first-party data
 *      (partner builders + home designs + suburb guides), linked
 *      via `citation`. Signals to AI parsers "this page's numbers
 *      come from a first-party dataset with a stable identity".
 *
 *   4) `mentions` array of the key entities named in the answer,
 *      so AI models can slot the page into their entity graph
 *      correctly (Adelaide, South Australia, RealEstateAgent, etc.).
 *
 * The schema is injected head-side via `useJsonLd` (bypasses Helmet
 * sanitisation) and also picked up by prerender.js when it renders
 * this component during SSG.
 *
 * @param {string} question - The visible question rendered in the
 *   QuickAnswer block. Must match verbatim for the schema pairing
 *   to work.
 * @param {string} answer - The plain-text version of the answer
 *   (strip HTML, keep it dense: numbers, place names, timeframes).
 * @param {string} url - The pathname of the page (e.g.
 *   `/building-costs-adelaide-2026`).
 * @param {string[]} [mentions] - Optional list of entity names to
 *   attach as `mentions` (e.g. ["Adelaide", "New Home Construction",
 *   "Building Contracts"]). BuildPilot + South Australia are
 *   added automatically.
 * @param {string} [dateModified] - ISO date the answer was last
 *   verified. Defaults to today.
 */
const AIOverviewSchema = ({
  question: _question,
  answer: _answer,
  url,
  mentions: _mentions = [],
  dateModified,
}) => {
  // Full ISO 8601 with UTC offset. Google's Q&A validator rejects
  // bare `YYYY-MM-DD` strings with "invalid datetime" + "missing a
  // timezone" warnings.
  const _todayInput = dateModified || new Date().toISOString();
  const today = /^\d{4}-\d{2}-\d{2}$/.test(_todayInput)
    ? `${_todayInput}T00:00:00Z`
    : _todayInput;
  const pageUrl = `${SITE_URL}${url}`;

  // 1) Speakable schema - voice-assistant surface.
  const speakableSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${pageUrl}#ai-overview`,
    url: pageUrl,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: [
        '[data-testid="quick-answer"]',
        '[data-ai-answer="true"]',
      ],
    },
  };

  // 2) QAPage schema was previously emitted here as a hydration
  //    fallback for pre-Emergent-fix days when non-JS crawlers only
  //    saw the SPA root index.html. Since Emergent's Feb 2026 fix,
  //    per-route baked HTML now serves natively AND the baked
  //    prerender.js emits its own QAPage with @id `#ai-qapage`. If we
  //    kept emitting a runtime QAPage with the same @id, GSC merged
  //    them and flagged "Duplicate field mainEntity" (critical). So
  //    we ONLY emit Dataset + Speakable at runtime now; QAPage lives
  //    in the baked HTML.

  // 3) Dataset entity - the "source of record" node LLMs cite.
  //    Same @id on every page so AI parsers see ONE canonical dataset
  //    referenced from many answers (strong entity signal).
  //    creator / publisher expanded inline (not @id refs) - GSC's
  //    per-block validator rejects bare @id refs on these fields.
  const orgInline = {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: businessIdentity.brand,
    url: businessIdentity.url,
    logo: businessIdentity.logo,
  };
  const datasetSchema = {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    '@id': `${SITE_URL}/#dataset`,
    name: 'BuildPilot Adelaide Building Industry Dataset',
    description:
      `First-party dataset maintained by BuildPilot covering Adelaide partner builders, ${HOME_DESIGN_COUNT}+ indexed home designs, ${SUBURB_GUIDE_COUNT} Adelaide suburb guides, ${HBH_EPISODE_COUNT} Home Building Hub podcast episodes and ${BLOG_POST_COUNT} editorial guides. Updated continuously.`,
    url: `${SITE_URL}/about/editorial-process`,
    creator: orgInline,
    publisher: orgInline,
    license: `${SITE_URL}/terms`,
    inLanguage: 'en-AU',
    isAccessibleForFree: true,
    keywords: [
      'Adelaide home builders',
      'Adelaide building costs',
      'South Australian home construction',
      'Adelaide suburb guides',
      'House and land Adelaide',
      'Knockdown rebuild Adelaide',
    ],
    spatialCoverage: {
      '@type': 'Place',
      name: 'Adelaide, South Australia, Australia',
    },
    temporalCoverage: '2021-01-01/..',
    dateModified: today,
  };

  useJsonLd(
    [speakableSchema, datasetSchema],
    `ai-overview:${url}`,
  );
  return null;
};

export default AIOverviewSchema;
